import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { mfaService } from '../services/mfaService';
import { Mail, AlertCircle, Shield, Loader2 } from 'lucide-react';

export function MfaVerificationDialog({ open, email, type, onVerified, onCancel }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (!open || !email) return;
    setCode('');
    setError('');
    setSent(false);
    setResendCooldown(0);
    if (type === 'LOGIN' || type === 'SIGNUP') {
      setSent(true);
    } else {
      sendCode();
    }
  }, [open, email]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => setResendCooldown(prev => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const sendCode = async () => {
    setError('');
    try {
      await mfaService.sendCode(email, type);
      setSent(true);
      setResendCooldown(30);
    } catch (err) {
      setError(err.message || 'Failed to send verification code');
    }
  };

  const handleVerify = (e) => {
    e.preventDefault();
    setError('');
    if (code.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }
    onVerified(code);
  };

  const handleResend = () => {
    if (resendCooldown > 0) return;
    sendCode();
  };

  return (
    <Dialog open={open} onOpenChange={(open) => { if (!open) onCancel(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Shield className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-center text-xl">Two-Factor Authentication</DialogTitle>
          <DialogDescription className="text-center">
            {sent
              ? `A 6-digit code has been sent to ${email}`
              : 'Sending verification code...'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleVerify} className="space-y-4">
          {error && (
            <Alert variant="destructive" className="py-2">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-sm">{error}</AlertDescription>
            </Alert>
          )}

          {sent && (
            <Alert className="py-2 bg-primary/5 border-primary/20">
              <Mail className="h-4 w-4 text-primary" />
              <AlertDescription className="text-sm">
                Check your email inbox for the code
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Input
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="text-center text-3xl tracking-[0.5em] h-14 font-mono"
              disabled={loading || !sent}
              autoComplete="one-time-code"
              autoFocus={sent}
            />
          </div>

          <Button
            type="submit"
            className="w-full h-11 text-base"
            disabled={loading || code.length !== 6}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Verifying...
              </>
            ) : (
              'Verify Code'
            )}
          </Button>

          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleResend}
              disabled={resendCooldown > 0 || loading}
              className="text-xs"
            >
              {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onCancel}
              disabled={loading}
              className="text-xs text-muted-foreground"
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default MfaVerificationDialog;
